export const FAILUREDETAILSSTEPS = {
    FAILURETYPES: 1,
    AREAS: 2,
    FUNCTIONALLOCATION: 3,
    FAILURECODES: 4,
    FAILUREDETAILSSELECTIONS: 5,
    DOWNTIMESAVED: 6,
    PLCSHOWHOMEDURINGDOWNTIME: 7,
};

export const MEASURE_METRICS = {
    DOWNTIME: 1,
    UPTIME: 2,
};

export const DOWNTIMEMETHODCAPTURE = {
    MANUAL: 1,
    PLC: 2,
};

export const USERTYPE = {
    ADMINISTRATOR: 1,
    EXECUTIVE: 2,
    MANAGER: 3,
    OPERATIONS: 4,
    MAINTENANCE: 5,
    OPERATOR: 6,
    OSORNOADMINISTRATOR: 7,
    API_ADMINISTRATOR: 8,
};

/** Hasura role names as emitted in the JWT's x-hasura-default-role claim. */
export const HASURA_ROLE = {
    API_ADMINISTRATOR: "api_administrator",
    ADMINISTRATOR: "administrator",
    OSORNO_ADMINISTRATOR: "osorno_administrator",
};

/**
 * User types that belong to a Corporation rather than a single mill, and so are created with a
 * null mill_id. Their rows are surfaced in every mill's Users tab (see getPaginatedUsers).
 */
export const CORPORATE_SCOPE_USER_TYPE_IDS = [USERTYPE.ADMINISTRATOR, USERTYPE.API_ADMINISTRATOR];

/**
 * User types only a Corporate Admin or Madera staff may assign. Both grant Corporation-wide
 * reach, so a mill-scoped manager must not be able to create one.
 */
export const RESTRICTED_ASSIGNABLE_USER_TYPE_IDS = [USERTYPE.ADMINISTRATOR, USERTYPE.API_ADMINISTRATOR];

/**
 * User rows a mill-scoped user must not be able to edit from a mill's Users tab. Superset of
 * RESTRICTED_ASSIGNABLE_USER_TYPE_IDS (the types a manager may not assign) plus Madera staff,
 * whose rows a customer must never edit. Kept derived so adding a Corporation-wide type in one
 * place cannot leave the edit guard behind.
 */
export const PROTECTED_USER_TYPE_IDS = [...RESTRICTED_ASSIGNABLE_USER_TYPE_IDS, USERTYPE.OSORNOADMINISTRATOR];

/**
 * User types that can own or be assigned operational work, and so may appear in people pickers
 * (RCA owners, assignees, action-plan owners, RCA filters, default-owner settings).
 *
 * Deliberately an allowlist. These lists used to be built as `Object.values(USERTYPE).filter(...)`
 * exclusions, which silently opts every newly added user type in: that is how the API
 * Administrator — who has no mill, no operational role and cannot read RCA data — reached the RCA
 * dropdowns the moment the type was added. Mill scoping does not catch it either, because the
 * people-picker queries widen to `mill_id _eq $millId OR mill_id IS NULL` and this type is
 * null-mill by design, so it would surface in every mill.
 *
 * The RCA Settings default-owner picker was the one exception: RCASettingsEntity screens its
 * options a second time on `owner.mill_id === productionLine.mill.mill_id`, which happens to drop
 * every null-mill type. That is a coincidence of that one component, not a guard.
 */
export const ASSIGNABLE_USER_TYPE_IDS = [
    USERTYPE.ADMINISTRATOR,
    USERTYPE.EXECUTIVE,
    USERTYPE.MANAGER,
    USERTYPE.OPERATIONS,
    USERTYPE.MAINTENANCE,
];

/** RCA team membership additionally includes Operators, who take part in the analysis itself. */
export const RCA_TEAM_MEMBER_USER_TYPE_IDS = [...ASSIGNABLE_USER_TYPE_IDS, USERTYPE.OPERATOR];

export const CORPORATE_BACKOFFICE_PATHS = {
    ROOT_PATH: "corporate_backoffice",
    LOGIN: "login",
    FORGOT_PASSWORD: {
        ROOT: "forgot_password",
        RECOVERY: "recovery",
    },
    SETUP_DASHBOARD: "setup_dashboard",
    CORPORATE_DASHBOARD: "corporate_dashboard",
    LOCATIONS: {
        ROOT: "locations",
        VARIABLE: ":millId",
        HASH_PATHS: {
            DASHBOARDS: "dashboards",
            PRODUCTION_LINES: "production_lines",
            CLUSTERS: "clusters",
            USERS: "users",
            SETTINGS: "settings",
            LOCAL_SERVER: "local_server",
        },
    },
    MACHINE_CENTER_DETAILS: {
        ROOT: "machine_center_details",
        VARIABLE: ":machineCenterId",
    },
    DOWNTIME_MODULE_HELP: "help",
    RCA: {
        ROOT: "rca",
        DASHBOARD: "dashboard",
        SETTINGS: "settings",
        COMPLIANCE: "compliance",
        CORPORATE_DASHBOARD: "corporate_dashboard",
        ISSUES: {
            ROOT: "issues",
            VARIABLE: ":issueId",
            FROM_ACTION_PLAN: "from=action_plan",
        },
        RCA_LIBRARY: {
            ROOT: "rca_library",
            VARIABLE: ":rcaId",
        },
        RCA_ACTION_PLANS: {
            ROOT: "rca_action_plans",
            VARIABLE: ":actionPlanId",
        },
    },
    API_KEY_MANAGEMENT: "api_key_management",
};

export const HMI_PATHS = {
    ROOT_PATH: "HMI",
    LOGIN: "login",
    DASHBOARD: "dashboard",
    CLUSTER: "cluster",
};

/**
 * The API Administrator portal (OS-2557) — a self-contained surface for the external integrator
 * who manages a Corporation's API keys. Deliberately outside CORPORATE_BACKOFFICE_PATHS: it does
 * not mount CorporateContextProvider, so none of the CBO shell's Hasura queries run for this role.
 */
export const API_ADMIN_PATHS = {
    ROOT_PATH: "api_administration",
    LOGIN: "login",
    KEYS: "keys",
};

export const MADERA_BACKOFFICE_PATHS = {
    ROOT_PATH: "madera_backoffice",
    LOGIN: "login",
    SETUP_DASHBOARD: {
        ROOT: "setup_dashboard",
        CORPORATION: {
            ROOT: "setup_dashboard/corporation",
            VARIABLE: ":corporationId",
        },
    },
    API_ACCESS: "api_access",
};

export const HMI_PLC_STATES = {
    RUNNING: "running",
    NOT_RUNNING: "notRunning",
    NOT_CONNECTED: "disconnected",
};

export const FAILURE_TYPES_CODE_EXCEPTIONS = {
    BELOW_TRIGGER: "BELOW_TRIGGER",
    NOT_SELECTED: "NOT_SELECTED",
};

export const OPERATOR_EXCEPTIONS = {
    NO_OPERATOR: "NO_OPERATOR",
};

export const FAILURE_CODE_EXCEPTIONS = {
    UNKNOWN: "UNKNOWN",
    NOT_SELECTED: "NOT_SELECTED",
};

export const allElements = "$$all$$";
export const notSelected = "$$notSelected$$";

export const UPDATEMINUTESRATEFORHMIDATA = Number(process.env.REACT_APP_HMI_UPDATE_RATE_MINUTES || 10) * 60000;

export const CHECKBACKENDCONNECTIONINTERVAL =
    Number(process.env.REACT_APP_CHECK_BACKEND_CONNECTION_INTERVAL || 5) * 1000;

export const TIMEOUTBACKENDCONNECTIONREQUEST =
    Number(process.env.REACT_APP_TIMEOUT_BACKEND_CONNECTION_REQUEST || 3) * 1000;

export const END_OF_SHIFT_FAILURE_CODE_NAME = "END_OF_SHIFT";

export const SUMMARY_REPORT_POPOVER_ID = "summary-report-popover-id";

export const CORPORATE_MC_POPOVER_ID = "corporate-mc-popover-id";
export const CORPORATE_FL_POPOVER_ID = "corporate-fl-popover-id";
export const CORPORATE_SECONDARY_MC_POPOVER_ID = "corporate-secondary-mc-popover-id";

export const USA_COUNTRY_CODE = "US";
export const USA_COUNTRY_ID = 1;

// ======= RCA MODULE CONSTANTS =======

export const RCA_STATUSES = {
    NOT_STARTED: 1,
    DONE_25: 2,
    DONE_50: 3,
    DONE_75: 4,
    COMPLETED: 5,
    NEEDS_APPROVAL: 6,
    BLOCKED: 7,
    REJECTED: 8,
};

export const RCA_PRIORITIES = {
    LOW: 1,
    MEDIUM: 2,
    HIGH: 3,
};

export const RCA_PRIORITIES_KEY_TRANSLATIONS = {
    1: "low",
    2: "medium",
    3: "high",
};

export const documentsFileTypeExtensions = {
    "document/pdf": [".pdf"],
    "document/doc": [".doc"],
    "document/docx": [".docx"],
    "document/txt": [".txt"],
    "document/xls": [".xls", ".xlsx"],
    "document/csv": [".csv"],
};

export const mediaFileTypeExtensions = {
    "image/png": [".png"],
    "image/jpeg": [".jpeg"],
    "image/jpg": [".jpg"],
    "video/mov": [".mov"],
    "video/mp4": [".mp4"],
};

export const allFileTypeExtensions = {
    ...documentsFileTypeExtensions,
    ...mediaFileTypeExtensions,
};

export const documentsFileTypeExtensionsRegExp = new RegExp(
    "^(pdf|PDF|doc|DOC|docx|DOCX|txt|TXT|xls|XLS|xlsx|XLSX|csv|CSV)$",
);

export const mediaFileTypeExtensionsRegExp = new RegExp("^(png|PNG|jpeg|JPEG|jpg|JPG|mov|MOV|mp4|MP4)$");

export enum SummaryOption {
    BY_TYPE = "type",
    BY_CODE = "code",
    BY_SECONDARY_CODE = "secondary code",
    ALL = "all",
}

export enum DisplayInOptionsEnum {
    PERCENT = "percent",
    MINUTES = "minutes",
    OCCURRENCES = "occurrences",
}

export const defaultCountryCode = "US";

export enum MachineCenterLogTriggersEnum {
    AT_BELOW_TRIGGER = "at below trigger",
    AT_DOWNTIME_CONTINUED_BELOW_TRIGGER = "at downtime continued below trigger",
    AT_DOWNTIME_CONTINUED_OVER_TRIGGER = "at downtime continued over trigger",
    AT_DOWNTIME_NOT_REGISTERED = "at downtime not register, below minimum definition",
    AT_DOWNTIME_NOT_SELECTED = "at downtime not selected",
    AT_DOWNTIME_INITIATED = "at downtime initiated",
    AT_UPTIME_INITIATED = "at uptime initiated",
    AT_DOWNTIME_EVENT_SENT = "at downtime event sent",
    AT_HMI_LOGIN = "at hmi login",
    AT_EDIT_DOWNTIME_MENU_OPEN = "at edit downtime menu open",
    AT_EDIT_DOWNTIME_SAVE = "at edit downtime save",
    AT_DOWNTIME_EVENT_CREATION_CANCEL = "at downtime event creation cancel",
    AT_DOWNTIME_EVENT_EDITING_CANCEL = "at downtime event editing cancel",
    AT_APP_UPDATE = "at app update",
    AT_INTERNET_CONNECTION_RESTORED = "at internet connection restored",
}

export enum DeviceType {
    MOBILE = "mobile",
    TABLET = "tablet",
    DESKTOP = "desktop",
}

export enum OperativeSystem {
    MACOS = "Mac OS",
    IOS = "iOS",
    WINDOWS = "Windows",
    ANDROID = "Android",
    LINUX = "Linux",
}

export enum NoDivisionEnum {
    NO_DIVISION = "NO_DIVISION",
}

export const CHANGE_PASSWORD_ERROR_CODES = {
    PASSWORD_INCORRECT: "PASSWORD_INCORRECT",
    PASSWORDS_DONT_MATCH: "PASSWORDS_DONT_MATCH",
    PASSWORD_INVALID: "PASSWORD_INVALID",
};

export const currentUserCorporationSelection = "$$currentUserCorporation$$";
export const currentUserDivisionSelection = "$$currentUserDivision$$";
export const currentUserRegionSelection = "$$currentUserRegion$$";
export const currentUserMillSelection = "$$currentUserMill$$";

export const RESET_PASSWORD_ERROR_CODES = {
    PASSWORDS_DONT_MATCH: "PASSWORDS_DONT_MATCH",
    PASSWORD_INVALID: "PASSWORD_INVALID",
    TOKEN_INVALID: "TOKEN_INVALID",
    EMAIL_ALREADY_SENT: "EMAIL_ALREADY_SENT",
};
export const DOWNTIME_DETAIL_REPORT_STOP_DATE_TIMEZONE = "MST";

export enum ServerResponseErrorCodes {
    BAD_REQUEST = 400,
    UNAUTHORIZED = 401,
    FORBIDDEN = 403,
    NOT_FOUND = 404,
    INTERNAL_SERVER_ERROR = 500,
}

export enum IconsTrafficLightStatusCodes {
    DOWN = "down",
    UP = "up",
    OFFLINE = "offline",
    END_OF_SHIFT = "end_of_shift",
    BELOW_TRIGGER = "below_trigger",
}

export enum LocalStorageCachedDataKeysHMI {
    MACHINE_CENTER = "machine_center",
    FAILURE_TYPES = "failure_types",
    DATA_LAST_UPDATED = "data_last_updated",
    EVENT_REPORT_EDITING = "event_report_editing",
    IS_DOWNTIME_DETAILS_INPUT = "is_downtime_details_input",
    EVENT_REPORT_EDIT_DATA = "event_report_edit_data",
    MQTT_BROKER_SETTINGS = "mqtt_broker_settings",
    LAST_TWO_SHIFT_EVENTS = "last_two_shifts_events",
    UPTIME = "uptime",
    PLC_DISCONNECTED = "plc_disconnected",
    OPERATOR = "operator",
    CURRENT_START_DOWNTIME = "count",
    FAILURE_DETAILS_STEP = "failure_details_step",
    FAILURE_TYPE = "failure_type",
    AREA = "area",
    FUNCTIONAL_LOCATION = "functional_location",
    FAILURE_CODE = "failure_code",
    NOTES = "notes",
    MACHINE_CENTER_HMI = "machine_center_hmi",
    HMI_DISPLAY_UNIT = "hmi_display_unit",
    BELOW_TRIGGER_DOWNTIME_COUNT = "below_trigger_downtime_count",
    LAST_INTERNET_CONNECTION_RECOVERY_DATE = "last_internet_connection_recovery_date",
    UUID_LOCALSERVER_DOWNTIME_CODE = "uuid_localserver_downtime_code",
    UUID_LOCALSERVER_DOWNTIME_CODE_DURING_INPUT = "uuid_localserver_downtime_code_during_input",
    NOT_SELECTED_EVENT_REPORT_ID = "not_selected_event_report_id",
    DEFAULT_UPTIME_VALUES = "default_uptime_values",
    EDITIONS_PENDING = "editions_pending",
    MANUAL_QUALITY_QUEUE = "manual_quality_queue",
    MANUAL_QUALITY_LAST_ACK = "manual_quality_last_ack",
    MANUAL_QUALITY_PENDING = "manual_quality_pending",
    SPLIT_IN_PROGRESS = "split_in_progress",
    SPLIT_START_DOWNTIME = "split_start_downtime",
    SPLIT_ORIGINAL_START_DOWNTIME = "split_original_start_downtime",
    SPLIT_ORIGINAL_SELECTIONS = "split_original_selections",
}

export enum LocalStorageCachedDataKeysCluster {
    CLUSTER_DETAILS = "cluster_details",
    MACHINE_CENTERS_CLUSTER = "machine_centers_cluster",
    CLUSTER_ID_HMI = "cluster_id_hmi",
    MACHINE_CENTERS_CLUSTER_STATUS = "machine_centers_cluster_status",
}

export enum LocalStorageGlobalKeys {
    TOKEN = "token",
    USER_ID = "user_id",
    LANGUAGES = "languages",
    I18_LANGUAGE_SELECTION = "i18nextLng",
}

export enum LocalStorageCorporateBackofficeKeys {
    CORPORATE_ONBOARDING = "corporate_onboarding",
}

export enum LocalStorageTranslationsKeys {
    ES = "i18next_res_es-translation",
    EN = "i18next_res_en-translation",
    FR = "i18next_res_fr-translation",
}

export const ComplianceScore = {
    COMPLETED_ON_TIME: 2,
    PENDING: 0,
    COMPLETED_LATE: -1,
    PENDING_LATE: -2,
};

export enum ComplianceTypes {
    COMPLETED_ON_TIME = "completed_on_time",
    PENDING = "pending",
    COMPLETED_LATE = "completed_late",
    PENDING_LATE = "pending_late",
    NONE = "none",
}

export enum Languages {
    EN = "en",
    ES = "es",
    FR = "fr",
}

export enum LanguagesEnum {
    EN = 1,
    ES = 2,
    FR = 3,
}

export const LanguageCodeMap = {
    [Languages.EN]: LanguagesEnum.EN,
    [Languages.ES]: LanguagesEnum.ES,
    [Languages.FR]: LanguagesEnum.FR,
};

export const ReverseLanguageCodeMap = {
    [LanguagesEnum.EN]: Languages.EN,
    [LanguagesEnum.ES]: Languages.ES,
    [LanguagesEnum.FR]: Languages.FR,
};

export enum DefaultTranslationFieldsInTable {
    FAILURE_TYPE = "default_failure_type_translations",
    FAILURE_CODE = "default_failure_code_translations",
    DAY = "default_day_translations",
    KEY_SUCCESS_FACTOR = "default_key_success_factor_translations",
    MEASURE_METRIC = "default_measure_metric_translations",
    USER_TYPE = "default_user_type_translations",
    STATUS = "default_status_translations",
    PRIORITY = "default_priority_translations",
    RCA_FREQUENCY_TRIGGER_UNIT = "default_rca_frequency_trigger_unit_translations",
    EMAIL_NOTIFICATION_TYPE = "default_email_notification_type_translations",
}

export const FIXED_ITEM_MARGIN = 12;
export const FIXED_MAIN_HORIZONTAL_LINE_HEIGHT = 4;
export const FIXED_MAIN_CATEGORY_HEIGHT = 48;
export const FIXED_ARROW_HEIGHT = 18;

export enum RcaTypeEnum {
    BY_DURATION = 1,
    BY_FREQUENCY = 2,
    MANUAL = 3,
}

export enum RcaFrequencyTriggerUnitEnum {
    SHIFT = 1,
    DAY = 2,
    WEEK = 3,
    MONTH = 4,
}

export const TRUNCATE_DESCRIPTION_LENGTH_FISHBONE_DIAGRAM = 15;
export const TRUNCATE_PROBLEM_STATEMENT_LENGTH_FISHBONE_DIAGRAM = 120;

export enum DefaultFailureTypesIds {
    ADMIN_SAFETY = 1,
    BREAK_END_OF_SHIFT = 2,
    EXTERNAL = 3,
}

export const PDFWorker = "https://unpkg.com/pdfjs-dist@4.7.76/build/pdf.worker.min.mjs";

export const NOTES_MAX_INPUT_HMI = 255;

// A downtime must be LONGER than this to be splittable (eligibility).
export const SPLIT_MIN_EVENT_DURATION_MS = 2 * 60 * 1000;
// Each resulting segment must be at least this long (in-modal min duration).
export const SPLIT_MIN_SEGMENT_DURATION_MS = 1 * 60 * 1000;

export const DEFAULT_INACTIVITY_LIMIT_CLUSTER_HMI = Number(
    process.env.REACT_APP_DEFAULT_INACTIVITY_LIMIT_CLUSTER_HMI || 120,
);

export const DEFAULT_INACTIVITY_ALERT_DELAY_CLUSTER_HMI = Number(
    process.env.REACT_APP_DEFAULT_INACTIVITY_ALERT_DELAY_CLUSTER_HMI || 60,
);

export enum ModuleNotificationsEnum {
    ASPEN = 1,
    BIRCH = 2,
}

export enum EmailNotificationTypesEnum {
    RECENTLY_ASSIGNED = 1,
    COMING_DUE = 2,
    PAST_DUE = 3,
    RECENTLY_CHANGED = 4,
    SECONDARY_CODES_AVAILABLE = 5,
    MACHINE_CENTER_DOWNTIME_TRIGGER = 6,
}

export const maxExcelGenerationRange = Number(process.env.REACT_APP_EXCEL_GENERATION_MAX_DAYS_RANGE) || 31;

export const CHECK_LOCALSERVER_CONNECTIVITY_INTERVAL =
    Number(process.env.REACT_APP_CHECK_LOCALSERVER_CONNECTIVITY_INTERVAL_SECONDS || 30) * 1000;

export const CHECK_RESPONSE_TIMEOUT_LOCALSERVER_INTERVAL =
    Number(process.env.REACT_APP_RESPONSE_TIMEOUT_LOCALSERVER_INTERVAL_SECONDS || 5) * 1000;

export const PENDING_SECONDARY_CODES_POLL_INTERVAL =
    Number(process.env.REACT_APP_PENDING_SECONDARY_CODES_POLL_INTERVAL_SECONDS || 60) * 1000;

export enum MQTTEventsEnum {
    ERROR = "error",
    CONNECT = "connect",
    MESSAGE = "message",
    CLOSE = "close",
}

export const HMI_REQUEST_TOPIC = process.env.REACT_APP_HMI_REQUEST_TOPIC || "hmi-request";
export const HMI_RESPONSE_TOPIC_PREFIX = process.env.REACT_APP_HMI_RESPONSE_TOPIC_PREFIX || "hmi-response-";
export const CLUSTER_REQUEST_TOPIC = process.env.REACT_APP_CLUSTER_REQUEST_TOPIC || "cluster-request";
export const CLUSTER_RESPONSE_TOPIC_PREFIX = process.env.REACT_APP_CLUSTER_RESPONSE_TOPIC_PREFIX || "cluster-response-";
export const KEEP_ALIVE_TOPIC = process.env.REACT_APP_KEEP_ALIVE_TOPIC || "keep-alive-localserver";
export const MANUAL_QUALITY_REQUEST_TOPIC =
    process.env.REACT_APP_MANUAL_QUALITY_REQUEST_TOPIC || "manual-quality-request";
export const MANUAL_QUALITY_RESPONSE_TOPIC_PREFIX =
    process.env.REACT_APP_MANUAL_QUALITY_RESPONSE_TOPIC_PREFIX || "manual-quality-response-";
export const HMIS_ALIVE_STATUS_TOPIC_PREFIX =
    process.env.REACT_APP_HMIS_ALIVE_STATUS_TOPIC_PREFIX || "hmis-alive-status";

export enum LocalserverStatusEnum {
    CONNECTED = 1,
    DISCONNECTED = 2,
    DEACTIVATED = 3,
}

export enum FiltersPreferencesReportsEnum {
    MILL_DASHBOARD_SUMMARY = 1,
    MILL_DASHBOARD_TREND = 2,
    MILL_DASHBOARD_DETAILS = 3,
    CORPORATE_DASHBOARD_SUMMARY = 4,
    RCA_LIBRARY = 5,
    RCA_ISSUES = 6,
    RCA_ACTION_PLANS = 7,
    RCA_COMPLIANCE = 8,
    RCA_CORPORATE_DASHBOARD = 9,
}

export const NAME_MAX_INPUT_RCA_ACTION_PLAN = 255;
